import { API_URL } from "../config";
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Video, Send, Globe, CheckCircle2, Facebook, Instagram } from 'lucide-react';



const UnlockLink = () => {
  const { id } = useParams();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [unlockedUrl, setUnlockedUrl] = useState('');

  React.useEffect(() => {
    const fetchLinkData = async () => {
      try {
        const response = await axios.get(`${API_URL}/links/${id}`);
        setLinkData(response.data);
      } catch (err) {
        setError('Link not found or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchLinkData();
  }, [id]);

  const handleStepClick = (index, url) => {
    window.open(url, '_blank');

    // Track the click in the background
    axios.post(`${API_URL}/links/${id}/click`).catch(() => {});

    if (!completedSteps.includes(index)) {
      setCompletedSteps([...completedSteps, index]);
    }
  };

  const handleUnlock = async () => {
    setVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/links/${id}/verify`, {
        completedSteps
      });
      setUnlockedUrl(response.data.destinationUrl);
      window.location.href = response.data.destinationUrl;
    } catch (err) {
      alert(err.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  if (!linkData) return null;

  const allStepsCompleted = completedSteps.length === linkData.steps.length;

  const getStepIcon = (type) => {
    switch(type) {
      case 'youtube': return <Video className="h-6 w-6 text-red-600" />;
      case 'telegram': return <Send className="h-6 w-6 text-blue-500" />;
      case 'facebook': return <Facebook className="h-6 w-6 text-blue-600" />;
      case 'instagram': return <Instagram className="h-6 w-6 text-pink-500" />;
      default: return <Globe className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStepText = (type) => {
    switch(type) {
      case 'youtube': return 'Subscribe to YouTube Channel';
      case 'telegram': return 'Join Telegram Channel';
      case 'facebook': return 'Follow on Facebook';
      case 'instagram': return 'Follow on Instagram';
      default: return 'Visit Website';
    }
  };

  const theme = linkData.theme || {
      bgColor: '#000000', // Default gray-900
      textColor: '#ffffff', // Default white
      buttonColor: '#3f0158', // Default blue-500
      animation: 'none'
  };

  const getAnimationClass = () => {
    if (theme.animation === 'pulse') return 'animate-pulse';
    if (theme.animation === 'bounce') return 'animate-bounce';
    return '';
  };

  return (
    <div 
        className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 bg-cover bg-center bg-no-repeat relative" 
        style={{ 
            backgroundColor: theme.bgColor, 
            color: theme.textColor,
            backgroundImage: theme.bgImage ? `url(${theme.bgImage})` : 'none'
        }}
    >
      {theme.bgImage && <div className="absolute inset-0 bg-black/50 z-0 backdrop-blur-sm" />}
      <div className="max-w-md w-full bg-black/40 rounded-2xl shadow-2xl p-8 border border-white/20 backdrop-blur-md relative z-10">
        <h2 className="text-2xl font-bold text-center mb-2" style={{ color: theme.textColor }}>{linkData.title}</h2>
        <p className="text-center opacity-70 mb-8" style={{ color: theme.textColor }}>Complete the steps below to unlock your link</p>

        <div className="space-y-4 mb-8">
          {linkData.steps.map((step, index) => {
            const isCompleted = completedSteps.includes(index);
            return (
              <button
                key={index}
                onClick={() => handleStepClick(index, step.url)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border ${
                  isCompleted
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : `bg-white/5 border-white/10 hover:bg-white/10 hover:-translate-y-1 ${getAnimationClass()}`
                } transition-all duration-200 shadow-sm`}
                style={
                  !isCompleted 
                    ? { backgroundColor: theme.buttonColor, color: theme.textColor, borderColor: 'transparent' } 
                    : {}
                }
              >
                <div className="flex items-center gap-4">
                  {getStepIcon(step.type)}
                  <span className="font-medium">{getStepText(step.type)}</span>
                </div>
                {isCompleted && <CheckCircle2 className="h-5 w-5 text-green-500" />}
              </button>
            )
          })}
        </div>

        {unlockedUrl ? (
          <a
            href={unlockedUrl}
            className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-sm text-lg font-bold transition-opacity hover:opacity-90 ${getAnimationClass()}`}
            style={{ backgroundColor: theme.buttonColor, color: theme.textColor }}
          >
            Proceed to Link
          </a>
        ) : (
          <button
            onClick={handleUnlock}
            disabled={!allStepsCompleted || verifying}
            className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-sm text-lg font-bold transition-all ${
              allStepsCompleted
                ? `hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] ${getAnimationClass()}`
                : 'cursor-not-allowed opacity-50'
            }`}
            style={{ backgroundColor: theme.buttonColor, color: theme.textColor }}
          >
            {verifying ? 'Verifying...' : 'Unlock Link'}
          </button>
        )}
      </div>
    </div>
  );
};

export default UnlockLink;